import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  UploadSimple,
  FileText,
  Sparkle,
  Waveform,
  CheckCircle,
  ArrowRight,
  SpeakerHigh,
  Eye,
  EyeSlash,
  X,
} from "@phosphor-icons/react";
import { UPLOAD } from "@/constants/testIds";
import { UserMenu } from "@/components/UserMenu";
import { makeT, normalizeLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { UPLOAD_STRINGS, uploadPickStrings, getSampleJdCategories, getSampleJdLevels, SAN_KEY_BY_CODE, PROG_KEY_BY_STAGE } from "@/lib/uploadI18n";
import { englishResumeRequiredMessage } from "@/lib/resumeLanguageI18n";
import { VOICE_SAMPLE_STRINGS } from "@/lib/voiceSampleI18n";
import { INTERVIEW_MODE_STRINGS, canChooseMode, modeOptionAvailable } from "@/lib/interviewModeI18n";
import {
  sanitizeResume,
  SanitizerError,
  SANITIZE_ERROR_CODES,
  SANITIZE_STAGES,
} from "@/lib/resumeSanitizer";

const ACCEPT = ".pdf,.docx";

function FileDrop({ label, hint, file, onFile, testId, required, u, lang, purgeSource }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (purgeSource && inputRef.current) {
      inputRef.current.value = "";
    }
  }, [purgeSource]);

  const handle = async (f) => {
    if (!f) return;
    setError(null);
    setValidating(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (lang) fd.append("interface_language", lang);
      const { data } = await api.post("/validate-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.ok) {
        onFile(f);
      } else {
        setError(data?.detail || u.dropInvalid);
        onFile(null);
        if (inputRef.current) inputRef.current.value = "";
        toast.error(data?.detail || u.dropInvalid);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || u.dropCantValidate;
      setError(detail);
      onFile(null);
      if (inputRef.current) inputRef.current.value = "";
      toast.error(detail);
    } finally {
      setValidating(false);
    }
  };

  const borderClass = error
    ? "border-[color:var(--score-bad)] bg-[color:var(--score-bad)]/5"
    : drag
    ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5"
    : "border-[color:var(--border-stone)] bg-white";

  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) handle(f);
      }}
      className={`block cursor-pointer rounded-2xl p-8 border ${borderClass} hover:border-[color:var(--brand)] transition-colors`}
    >
      <input
        ref={inputRef}
        data-testid={testId}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => e.target.files?.[0] && handle(e.target.files[0])}
      />
      <div className="flex items-start gap-4">
        <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${
          error ? "bg-[color:var(--score-bad)]/10 text-[color:var(--score-bad)]" : "bg-[color:var(--brand)]/10 text-[color:var(--brand)]"
        }`}>
          {validating ? (
            <span className="h-3 w-3 rounded-full bg-current animate-pulse" />
          ) : error ? (
            <UploadSimple weight="duotone" size={22} />
          ) : file ? (
            <CheckCircle weight="duotone" size={26} />
          ) : (
            <UploadSimple weight="duotone" size={22} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm uppercase tracking-widest text-[color:var(--text-secondary)]">
            {required ? u.required : u.optional}
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-lg tracking-tight">{label}</div>
          </div>
          {validating ? (
            <div className="mt-2">
              <div className="text-sm text-[color:var(--text-secondary)] flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand)] animate-pulse" />
                {u.dropVerifying}
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-[color:var(--border-stone)] overflow-hidden">
                <div className="h-full w-1/3 rounded-full bg-[color:var(--brand)] validate-progress" />
              </div>
            </div>
          ) : error ? (
            <div className="mt-2 text-sm text-[color:var(--score-bad)] font-medium">
              {error}
            </div>
          ) : file ? (
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <div className="text-sm text-[color:var(--brand)] font-medium flex items-center gap-2 min-w-0 max-w-full">
                <FileText size={16} className="shrink-0" />
                <span className="truncate">{file.name}</span>
              </div>
              <button
                type="button"
                data-testid="resume-remove-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (inputRef.current) inputRef.current.value = "";
                  setError(null);
                  onFile(null);
                  toast.success(u.resumeReplaced);
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border-stone)] text-[color:var(--text-secondary)] hover:bg-[color:var(--score-bad)]/10 hover:border-[color:var(--score-bad)] hover:text-[color:var(--score-bad)] px-3 py-1 text-xs transition-colors"
                aria-label={u.resumeRemove}
              >
                <X size={12} weight="bold" />
                <span>{u.resumeRemove}</span>
              </button>
            </div>
          ) : (
            <div className="mt-2 text-sm text-[color:var(--text-secondary)]">{hint}</div>
          )}
          <div className="mt-4 inline-flex items-center gap-2 text-xs mono text-[color:var(--text-secondary)]">
            PDF · DOCX
          </div>
        </div>
      </div>
    </label>
  );
}

const JD_MAX = 4000;

function ChoiceChip({ selected, onClick, disabled, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-pressed={selected}
      className={`text-left px-4 py-2.5 rounded-full border text-sm transition-colors ${
        disabled ? "opacity-50 cursor-not-allowed " : ""
      }${
        selected
          ? "border-[color:var(--brand)] bg-[color:var(--brand)]/10 text-[color:var(--brand)] font-semibold"
          : "border-[color:var(--border-stone)] bg-white text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)]"
      }`}
    >
      {children}
    </button>
  );
}

function JobDescriptionField({
  value,
  onChange,
  mode,
  onModeChange,
  sampleGenerationMode,
  onSampleGenerationModeChange,
  resumeReady,
  category,
  onCategoryChange,
  level,
  onLevelChange,
  extraContext,
  onExtraContextChange,
  hasGenerated,
  generating,
  onGenerate,
  u,
  categories,
  levels,
  lang,
}) {
  const [error, setError] = useState("");
  const isSample = mode === "sample";
  const useResume = sampleGenerationMode === "resume";

  const handlePaste = (e) => {
    if (isSample) return;
    const pasted = (e.clipboardData || window.clipboardData)?.getData("text") ?? "";
    if (!pasted) return;
    const el = e.target;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + pasted + value.slice(end);
    if (next.length > JD_MAX) {
      e.preventDefault();
      setError(u.jdOverPaste);
      return;
    }
    setError("");
  };

  const handleChange = (e) => {
    if (isSample) return;
    const next = e.target.value;
    if (next.length >= JD_MAX) {
      onChange(next.slice(0, JD_MAX));
      setError(u.jdOverTyping);
      return;
    }
    if (error === u.jdOverTyping && next.length < JD_MAX) setError("");
    onChange(next);
  };

  const count = value.length;
  const overLimit = count >= JD_MAX;
  const canGenerate = isSample && !generating && (
    useResume ? resumeReady : Boolean(category) && Boolean(level)
  );

  return (
    <div className="rounded-2xl p-6 border border-[color:var(--border-stone)] bg-white">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-[color:var(--brand)]/10 text-[color:var(--brand)] flex items-center justify-center shrink-0">
          <FileText weight="duotone" size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm uppercase tracking-widest text-[color:var(--text-secondary)]">
            {u.jdRequired}
          </div>
          <div className="mt-1 font-semibold text-lg tracking-tight">
            {u.jdTitle}
          </div>

          <div className="mt-4 space-y-3">
            <label
              className="flex items-start gap-3 p-3 rounded-xl border border-[color:var(--border-stone)] hover:bg-[color:var(--bg-surface-hover)] cursor-pointer"
              data-testid="jd-mode-real"
            >
              <input
                type="radio"
                name="jd-mode"
                value="real"
                checked={mode === "real"}
                onChange={() => onModeChange("real")}
                className="mt-1 accent-[color:var(--brand)]"
              />
              <span className="text-sm text-[color:var(--text-primary)] leading-relaxed">
                {u.jdModeReal}
              </span>
            </label>
            <label
              className="flex items-start gap-3 p-3 rounded-xl border border-[color:var(--border-stone)] hover:bg-[color:var(--bg-surface-hover)] cursor-pointer"
              data-testid="jd-mode-sample"
            >
              <input
                type="radio"
                name="jd-mode"
                value="sample"
                checked={mode === "sample"}
                onChange={() => onModeChange("sample")}
                className="mt-1 accent-[color:var(--brand)]"
              />
              <span className="text-sm text-[color:var(--text-primary)] leading-relaxed">
                {u.jdModeSample}
              </span>
            </label>
          </div>

          {isSample && (
            <div className="mt-6 space-y-5" data-testid="jd-sample-picker">
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">
                  {u.jdSampleChoiceH}
                </div>
                <div className="space-y-3">
                  <label
                    className="flex items-start gap-3 p-4 rounded-xl border border-[color:var(--border-stone)] hover:bg-[color:var(--bg-surface-hover)] cursor-pointer"
                    data-testid="jd-sample-from-resume"
                  >
                    <input
                      type="radio"
                      name="jd-sample-generation-mode"
                      value="resume"
                      checked={useResume}
                      onChange={() => onSampleGenerationModeChange("resume")}
                      disabled={generating}
                      className="mt-1 accent-[color:var(--brand)]"
                    />
                    <span>
                      <span className="block text-sm font-medium text-[color:var(--text-primary)]">{u.jdSampleFromResume}</span>
                      <span className="mt-1 block text-xs leading-relaxed text-[color:var(--text-secondary)]">{u.jdSampleFromResumeSub}</span>
                    </span>
                  </label>
                  <label
                    className="flex items-start gap-3 p-4 rounded-xl border border-[color:var(--border-stone)] hover:bg-[color:var(--bg-surface-hover)] cursor-pointer"
                    data-testid="jd-sample-specified"
                  >
                    <input
                      type="radio"
                      name="jd-sample-generation-mode"
                      value="specified"
                      checked={!useResume}
                      onChange={() => onSampleGenerationModeChange("specified")}
                      disabled={generating}
                      className="mt-1 accent-[color:var(--brand)]"
                    />
                    <span>
                      <span className="block text-sm font-medium text-[color:var(--text-primary)]">{u.jdSampleSpecified}</span>
                      <span className="mt-1 block text-xs leading-relaxed text-[color:var(--text-secondary)]">{u.jdSampleSpecifiedSub}</span>
                    </span>
                  </label>
                </div>
              </div>

              {useResume ? (
                !resumeReady && (
                  <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-[color:var(--text-primary)]" data-testid="jd-sample-resume-not-ready">
                    {u.jdSampleResumeNotReady}
                  </div>
                )
              ) : (
                <>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">
                      {u.jdCatH}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((c) => (
                        <ChoiceChip
                          key={c.id}
                          testId={`jd-cat-${c.id}`}
                          selected={category === c.id}
                          onClick={() => onCategoryChange(c.id)}
                          disabled={generating}
                        >
                          {c.label}
                        </ChoiceChip>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">
                      {u.jdLvlH}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {levels.map((l) => (
                        <ChoiceChip
                          key={l.id}
                          testId={`jd-lvl-${l.id}`}
                          selected={level === l.id}
                          onClick={() => onLevelChange(l.id)}
                          disabled={generating}
                        >
                          {l.label}
                        </ChoiceChip>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-2">
                      {u.jdExtraH}
                    </div>
                    <div className="text-xs text-[color:var(--text-secondary)] mb-3 leading-relaxed max-w-xl">
                      {u.jdExtraSub}
                    </div>
                    <textarea
                      data-testid="jd-extra-context"
                      value={extraContext || ""}
                      onChange={(e) => onExtraContextChange(e.target.value.slice(0, 400))}
                      disabled={generating}
                      placeholder={u.jdExtraPh}
                      rows={3}
                      maxLength={400}
                      className="w-full rounded-xl border border-[color:var(--border-stone)] bg-white px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)] resize-y"
                    />
                    <div className="mt-1 text-[11px] mono text-[color:var(--text-secondary)] text-right">
                      {(extraContext || "").length} / 400
                    </div>
                  </div>
                </>
              )}

              <div>
                <Button
                  type="button"
                  data-testid="jd-generate-sample-btn"
                  disabled={!canGenerate}
                  onClick={onGenerate}
                  className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white"
                >
                  {generating
                    ? u.jdGenBusy
                    : hasGenerated
                    ? u.jdGenAgain
                    : u.jdGen}
                </Button>
              </div>
            </div>
          )}

          {mode === "real" && (
            <div className="mt-6 text-sm text-[color:var(--text-secondary)] leading-relaxed space-y-2">
              <p>{u.jdRealP1}</p>
              <p>{u.jdRealP2}</p>
              <ul className="list-disc list-outside pl-5 space-y-0.5">
                <li>{u.jdRealBullet1}</li>
                <li>{u.jdRealBullet2}</li>
                <li>{u.jdRealBullet3}</li>
              </ul>
              <p>{u.jdRealP3}</p>
            </div>
          )}

          <textarea
            data-testid="job-description-textarea"
            value={value}
            onChange={handleChange}
            onPaste={handlePaste}
            disabled={isSample}
            placeholder={isSample ? u.jdSamplePlaceholder : u.jdRealPlaceholder}
            rows={8}
            className={`mt-4 w-full rounded-xl border border-[color:var(--border-stone)] px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)] resize-y ${
              isSample ? "bg-[color:var(--bg-surface-hover)] cursor-not-allowed" : "bg-white"
            }`}
          />
          <div className="mt-2 flex items-center justify-between gap-4">
            <div
              data-testid="job-description-error"
              className={`text-xs leading-snug ${error ? "text-[color:var(--score-bad)]" : "text-transparent"} min-h-[1rem]`}
            >
              {error || "\u00A0"}
            </div>
            <div
              data-testid="job-description-counter"
              className={`text-xs mono shrink-0 ${overLimit ? "text-[color:var(--score-bad)]" : "text-[color:var(--text-secondary)]"}`}
            >
              {count.toLocaleString(lang || undefined)} / {JD_MAX.toLocaleString(lang || undefined)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Upload() {
  const [resume, setResume] = useState(null);
  const [resumeText, setResumeText] = useState("");
  const [resumeMeta, setResumeMeta] = useState(null);
  const [resumeSanitizing, setResumeSanitizing] = useState(false);
  const [resumeSanitizeError, setResumeSanitizeError] = useState("");
  const [resumeShowPreview, setResumeShowPreview] = useState(false);
  const [resumeReviewAcked, setResumeReviewAcked] = useState(false);
  const [processingStage, setProcessingStage] = useState("");
  const [showIndicator, setShowIndicator] = useState(false);
  const indicatorShowTimerRef = useRef(null);
  const indicatorHideTimerRef = useRef(null);
  const clearIndicatorTimers = () => {
    if (indicatorShowTimerRef.current) {
      clearTimeout(indicatorShowTimerRef.current);
      indicatorShowTimerRef.current = null;
    }
    if (indicatorHideTimerRef.current) {
      clearTimeout(indicatorHideTimerRef.current);
      indicatorHideTimerRef.current = null;
    }
  };
  useEffect(() => () => clearIndicatorTimers(), []);
  const [jdText, setJdText] = useState("");
  const [jdMode, setJdMode] = useState("real");
  const [jdSampleGenerationMode, setJdSampleGenerationMode] = useState("resume");
  const [jdCategory, setJdCategory] = useState("");
  const [jdLevel, setJdLevel] = useState("");
  const [jdExtra, setJdExtra] = useState("");
  const [jdGeneratedLevel, setJdGeneratedLevel] = useState("");
  const [jdHasGenerated, setJdHasGenerated] = useState(false);
  const [jdGenerating, setJdGenerating] = useState(false);
  const [voice, setVoice] = useState("female");
  // "Hear this voice" (2026-10-30): the admin's sample phrase in the chosen voice.
  const [voiceSampleOn, setVoiceSampleOn] = useState(false);
  const [voiceSamplePlaying, setVoiceSamplePlaying] = useState(false);
  const voiceSampleAudio = useRef(null);
  useEffect(() => {
    api.get("/voice-sample").then(({ data }) => setVoiceSampleOn(Boolean(data?.available))).catch(() => {});
  }, []);
  const playVoiceSample = async () => {
    if (voiceSamplePlaying) return;
    setVoiceSamplePlaying(true);
    try {
      const { data } = await api.get(`/voice-sample/${voice}`, { timeout: 45000 });
      const bin = atob(data.audio_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: data.mime || "audio/mpeg" }));
      const el = voiceSampleAudio.current;
      if (!el) return;
      el.src = url;
      el.onended = () => { URL.revokeObjectURL(url); setVoiceSamplePlaying(false); };
      await el.play();
    } catch {
      setVoiceSamplePlaying(false);
      toast.error((VOICE_SAMPLE_STRINGS[accountLang] || VOICE_SAMPLE_STRINGS.en).error);
    }
  };
  const [showText, setShowText] = useState(true);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refresh } = useAuth();

  const firstName = user?.first_name || "";
  const isAdmin = Boolean(user?.is_admin);
  const creditBalance = Number(user?.credit_balance ?? 0);
  const outOfCredits = !isAdmin && creditBalance <= 0;

  const accountLang = normalizeLang(user?.preferred_language);
  const u = uploadPickStrings(accountLang);
  const t = makeT(accountLang);
  const nonEnglishResumeMessage = englishResumeRequiredMessage(accountLang);
  const sampleCategories = useMemo(() => getSampleJdCategories(accountLang), [accountLang]);
  const sampleLevels = useMemo(() => getSampleJdLevels(accountLang), [accountLang]);

  const localizedSanitizeMessages = useMemo(() => {
    const map = {};
    for (const [code, key] of Object.entries(SAN_KEY_BY_CODE)) map[code] = u[key];
    return map;
  }, [u]);
  const localizedProgressLabels = useMemo(() => {
    const map = {};
    for (const [stg, key] of Object.entries(PROG_KEY_BY_STAGE)) map[stg] = u[key];
    return map;
  }, [u]);

  const practiceFormat = location.state?.practice_format;
  // Guided or Live Interview, chosen in Session settings below (moved here 2026-10-30:
  // the format screen it was on can no longer be reached). Kept in session storage
  // too, so it survives a trip away from this screen and back. Free trial: Guided only.
  const [modeChoice, setModeChoice] = useState(() => {
    const fromState = location.state?.mode;
    if (fromState === "live" || fromState === "guided") return fromState;
    try { return sessionStorage.getItem("mec_interview_mode") === "live" ? "live" : "guided"; } catch { return "guided"; }
  });
  const showModeChoice = canChooseMode(practiceFormat);
  const interviewMode = showModeChoice ? modeChoice : "guided";
  const chooseMode = (next) => {
    setModeChoice(next);
    try { sessionStorage.setItem("mec_interview_mode", next); } catch { /* private window */ }
  };
  useEffect(() => {
    if (!practiceFormat) {
      navigate("/", { replace: true });
    }
  }, [practiceFormat, navigate]);

  useEffect(() => {
    const r = location.state?.restore;
    if (!r) return;
    if (typeof r.resumeText === "string" && r.resumeText) setResumeText(r.resumeText);
    if (r.resumeMeta) setResumeMeta(r.resumeMeta);
    if (typeof r.resumeReviewAcked === "boolean") setResumeReviewAcked(r.resumeReviewAcked);
    if (typeof r.jdText === "string") setJdText(r.jdText);
    if (r.jdMode) setJdMode(r.jdMode);
    if (r.jdSampleGenerationMode) setJdSampleGenerationMode(r.jdSampleGenerationMode);
    if (r.jdCategory) setJdCategory(r.jdCategory);
    if (r.jdLevel) setJdLevel(r.jdLevel);
    if (typeof r.jdExtra === "string") setJdExtra(r.jdExtra);
    if (r.jdGeneratedLevel) setJdGeneratedLevel(r.jdGeneratedLevel);
    if (typeof r.jdHasGenerated === "boolean") setJdHasGenerated(r.jdHasGenerated);
    if (r.voice) setVoice(r.voice);
    if (typeof r.showText === "boolean") setShowText(r.showText);
    navigate(location.pathname, { replace: true, state: {} });
  }, []);

  const handleResumeFile = async (file) => {
    setResumeText("");
    setResumeMeta(null);
    setResumeSanitizeError("");
    setResumeShowPreview(false);
    setResumeReviewAcked(false);
    if (jdMode === "sample" && jdSampleGenerationMode === "resume") {
      setJdText("");
      setJdGeneratedLevel("");
      setJdHasGenerated(false);
    }
    clearIndicatorTimers();
    setShowIndicator(false);
    setProcessingStage("");
    if (!file) {
      setResume(null);
      return;
    }
    setResume(file);
    setResumeSanitizing(true);
    indicatorShowTimerRef.current = setTimeout(() => {
      indicatorShowTimerRef.current = null;
      setShowIndicator(true);
    }, 1000);
    try {
      const { text, meta } = await sanitizeResume(file, {
        onStage: setProcessingStage,
      });
      let finalText = text;
      let flags = [];
      let findings = [];
      try {
        const fd = new FormData();
        fd.append("text", text);
        const { data } = await api.post("/resume/sanitize", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        finalText = (data && data.text) || text;
        flags = (data && data.flags) || [];
        findings = (data && data.findings) || [];
      } catch {
        // Network hiccup — keep the client-scrubbed text; the backend still
        // enforces both privacy and language policy on POST /api/sessions.
      }

      // English-language gate. This runs on the already extracted/sanitized
      // text, not the original résumé file. Only confidently non-English
      // documents are blocked; proper nouns and occasional foreign terms are
      // deliberately tolerated by the backend classifier.
      try {
        const languageFd = new FormData();
        languageFd.append("text", finalText);
        const { data: language } = await api.post("/resume/language-check", languageFd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (language && language.accepted === false) {
          setResumeSanitizeError(nonEnglishResumeMessage);
          setResume(null);
          setResumeText("");
          setResumeMeta(null);
          setResumeReviewAcked(false);
          clearIndicatorTimers();
          setProcessingStage("");
          setShowIndicator(false);
          toast.error(nonEnglishResumeMessage);
          return;
        }
      } catch {
        // A transient language-check failure must not strand the upload page.
        // The server repeats the same gate before question generation.
      }

      setResumeText(finalText);
      setResumeMeta({ ...meta, entityFlags: flags, piiFindings: findings });
      if (findings.length > 0) {
        toast.warning(
          u.toastCurriculumPiiFound ||
            "Please remove the highlighted identifying information from your résumé preview before continuing.",
        );
      } else {
        toast.success(u.toastCurriculumSanitized);
      }
      if (indicatorShowTimerRef.current) {
        clearTimeout(indicatorShowTimerRef.current);
        indicatorShowTimerRef.current = null;
        setProcessingStage("");
        setShowIndicator(false);
      } else {
        setProcessingStage(SANITIZE_STAGES.DONE);
        indicatorHideTimerRef.current = setTimeout(() => {
          indicatorHideTimerRef.current = null;
          setProcessingStage("");
          setShowIndicator(false);
        }, 600);
      }
    } catch (e) {
      const code = e instanceof SanitizerError ? e.code : SANITIZE_ERROR_CODES.PDF_CORRUPT;
      const msg = localizedSanitizeMessages[code] || localizedSanitizeMessages[SANITIZE_ERROR_CODES.PDF_CORRUPT];
      setResumeSanitizeError(msg);
      setResume(null);
      clearIndicatorTimers();
      setProcessingStage("");
      setShowIndicator(false);
      toast.error(msg);
    } finally {
      setResumeSanitizing(false);
    }
  };

  const handleResumeReviewAck = (checked) => {
    setResumeReviewAcked(checked);
    if (checked) {
      // The original File is needed only for local extraction/privacy scanning.
      // Once the user approves the sanitized text, discard the source File so
      // downstream flows and re-practice retain only the approved text.
      setResume(null);
    }
  };

  const generateSampleJd = async () => {
    const fromResume = jdSampleGenerationMode === "resume";
    if (fromResume) {
      if (!resumeText || !resumeReviewAcked) return;
    } else if (!jdCategory || !jdLevel) {
      return;
    }
    setJdGenerating(true);
    try {
      const payload = fromResume
        ? {
            generation_mode: "resume",
            resume_text: resumeText,
          }
        : {
            generation_mode: "specified",
            category: jdCategory,
            level: jdLevel,
            extra_context: jdExtra.trim(),
          };
      const { data } = await api.post("/sample-jd", payload);
      setJdText(data.text || "");
      setJdGeneratedLevel(fromResume ? "" : jdLevel);
      setJdHasGenerated(true);
      toast.success(u.toastJdGenerated);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "unsanitized_content_detected") {
        toast.error(u.toastCurriculumPiiFound);
      } else {
        toast.error(typeof detail === "string" ? detail : u.toastJdGenFail);
      }
    } finally {
      setJdGenerating(false);
    }
  };

  const seniorityHintForLevel = {
    junior: "entry",
    pleno: "mid",
    senior: "senior",
    lideranca: "managerial",
  };

  const submit = async () => {
    if (outOfCredits) {
      toast.error(u.errInsufficientCredits);
      return;
    }
    if (!resumeText || !resumeReviewAcked) {
      toast.error(u.errNoResume);
      return;
    }
    if (!jdText.trim()) {
      toast.error(
        jdMode === "sample" ? u.errNoJdSample : u.errNoJdReal,
      );
      return;
    }
    setBusy(true);
    setStage("reading");
    clearIndicatorTimers();
    setProcessingStage("preparing");
    setShowIndicator(false);
    indicatorShowTimerRef.current = setTimeout(() => {
      indicatorShowTimerRef.current = null;
      setShowIndicator(true);
    }, 1000);
    try {
      const fd = new FormData();
      fd.append("first_name", firstName.trim());
      fd.append("resume_text", resumeText);
      fd.append("job_description_text", jdText.trim());
      const isSample = jdMode === "sample" && jdHasGenerated;
      if (isSample) fd.append("is_sample_jd", "true");
      if (isSample && jdGeneratedLevel) {
        const hint = seniorityHintForLevel[jdGeneratedLevel];
        if (hint) fd.append("seniority_hint", hint);
      }
      fd.append("practice_format", practiceFormat);
      fd.append("voice", voice);
      fd.append("hide_text", String(!showText));
      fd.append("mode", interviewMode);
      const sessionId = `iv_${crypto.randomUUID().replace(/-/g, "").slice(0, 12)}`;
      fd.append("session_id_override", sessionId);
      setStage("writing");
      await api.post("/sessions", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      try { await refresh(); } catch {}
      navigate(`/prepare/${sessionId}`, { replace: false });
      return;
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 400 && detail && detail.code === "unsanitized_content_detected") {
        toast.error(
          u.toastCurriculumPiiFound ||
            "Please remove the highlighted identifying information from your résumé preview before continuing.",
        );
        setResumeMeta((m) => ({
          ...(m || {}),
          piiFindings: Array.isArray(detail.findings) ? detail.findings : [],
        }));
      } else if (status === 400 && detail && detail.code === "resume_not_english") {
        toast.error(nonEnglishResumeMessage);
        setResumeSanitizeError(nonEnglishResumeMessage);
        setResume(null);
        setResumeText("");
        setResumeMeta(null);
        setResumeReviewAcked(false);
      } else if (status === 402 && detail && detail.code === "insufficient_credits") {
        toast.error(u.errInsufficientCredits);
        try { await refresh(); } catch {}
      } else {
        const msg = typeof detail === "string" ? detail : u.errCreateSession;
        toast.error(msg);
      }
      setBusy(false);
      setStage("");
      clearIndicatorTimers();
      setProcessingStage("");
      setShowIndicator(false);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="max-w-6xl mx-auto px-8 md:px-12 py-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="/brand/logo.png"
            alt="English Job Interview Simulator"
            className="h-[100px] w-[100px] object-contain"
          />
          <div>
            <div className="flex items-center gap-2 leading-tight">
              <span className="font-semibold text-lg tracking-tight">English Job Interview Simulator</span>
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-4 text-xs uppercase tracking-widest text-[color:var(--text-secondary)]">
          <div className="flex items-center gap-2">
            <Sparkle size={14} className="text-[color:var(--brand)]" /> {u.poweredBy}
          </div>
          <div
            className="flex items-center gap-1.5 rounded-full border border-[color:var(--border-stone)] bg-[color:var(--brand)]/5 px-3 py-1"
            data-testid="credits-balance"
          >
            <span>{u.creditsBalance}</span>
            <span
              className="font-mono font-semibold text-[color:var(--brand)] text-sm tracking-normal normal-case"
              data-testid="credits-balance-value"
            >
              {user?.credit_balance ?? 0}
            </span>
          </div>
          <UserMenu />
        </div>
      </header>

      <main data-testid={UPLOAD.container} className="max-w-6xl mx-auto px-8 md:px-12 pb-32 sm:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <section className="lg:col-span-7">
          {firstName && (() => {
            const [prefix = "", suffix = ""] = String(u.welcomeHi).split("{name}");
            return (
              <p
                data-testid="upload-welcome-hi"
                className="text-2xl sm:text-3xl font-bold mb-3"
              >
                <span className="text-[color:var(--text-primary)]">{prefix}</span>
                <span
                  data-testid="upload-welcome-hi-name"
                  className="text-[color:var(--brand)]"
                >
                  {firstName}
                </span>
                {suffix && (
                  <span className="text-[color:var(--text-primary)]">{suffix}</span>
                )}
              </p>
            );
          })()}
          {outOfCredits && (
            <div
              className="mb-6 rounded-2xl border border-[color:var(--score-bad)]/40 bg-[color:var(--score-bad)]/5 p-4"
              data-testid="no-credits-banner"
              role="alert"
            >
              <div className="font-semibold text-[color:var(--score-bad)] mb-1">
                {u.noCreditsTitle}
              </div>
              <p className="text-sm text-[color:var(--text-primary)] leading-relaxed">
                {u.noCreditsBody}
              </p>
            </div>
          )}
          <p className="text-sm uppercase tracking-[0.2em] text-[color:var(--brand)] mb-4">
            {u.step1Eye}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            {u.heroH}<em className="not-italic text-[color:var(--brand)]">{u.heroEm}</em>{u.heroTail}
          </h1>
          <p className="mt-6 text-lg text-[color:var(--text-secondary)] max-w-xl leading-relaxed">
            {u.heroP1}<strong>{u.heroPStrong1}</strong>{u.heroP2}<strong>{u.heroPStrong2}</strong>{u.heroP3}
          </p>

          <section
            data-testid="how-it-works"
            className="mt-10 rounded-3xl border border-[color:var(--border-stone)] bg-white p-6 md:p-8"
          >
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-5">
              {u.howH}
            </div>
            <ol className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                [u.step1H, u.step1P],
                [u.step2H, u.step2P],
                [u.step3H, u.step3P],
              ].map((s, i) => (
                <li key={s[0]} className="flex gap-4">
                  <div className="h-8 w-8 shrink-0 rounded-full bg-[color:var(--brand)]/10 text-[color:var(--brand)] flex items-center justify-center mono font-semibold text-sm">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-semibold tracking-tight">{s[0]}</div>
                    <div className="text-sm text-[color:var(--text-secondary)] mt-0.5 leading-relaxed whitespace-pre-line">
                      {s[1]}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <div className="mt-10 space-y-4">
            <FileDrop
              label={u.resumeLabel}
              hint={u.resumeHint}
              file={resume || (resumeReviewAcked && resumeText ? { name: u.resumeLabel } : null)}
              onFile={handleResumeFile}
              testId={UPLOAD.resumeInput}
              required
              u={u}
              lang={accountLang}
              purgeSource={resumeReviewAcked}
            />
            {showIndicator && processingStage && (
              <div
                className="mt-3 flex items-center gap-2 text-xs text-[color:var(--text-secondary)]"
                data-testid="resume-processing-stage"
                role="status"
                aria-live="polite"
              >
                {processingStage === SANITIZE_STAGES.DONE ? (
                  <span className="text-[color:var(--brand)]" aria-hidden="true">✓</span>
                ) : (
                  <span className="inline-block h-2 w-2 rounded-full bg-[color:var(--brand)] animate-pulse" aria-hidden="true" />
                )}
                <span data-testid="resume-processing-stage-label">
                  {localizedProgressLabels[processingStage] || processingStage}
                </span>
              </div>
            )}
            {resumeSanitizeError && (
              <div
                className="mt-2 text-xs text-[color:var(--score-bad)] leading-relaxed"
                data-testid="resume-sanitize-error"
              >
                {resumeSanitizeError}
              </div>
            )}
            {resumeText && !resumeSanitizing && !resumeSanitizeError && (
              <div className="mt-2" data-testid="resume-sanitized-banner">
                <div className="text-xs text-[color:var(--brand)] leading-relaxed">
                  {u.resumeSanBanner}
                </div>
              </div>
            )}

            {!resumeSanitizing && !resumeSanitizeError && (
              <div className="mt-4" data-testid="resume-review-block">
                <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-1">
                  {u.required}
                </div>
                <div className="text-lg font-semibold tracking-tight text-[color:var(--text-primary)] mb-2">
                  {u.resumeReviewH}
                </div>
                <div
                  className="text-xs text-[color:var(--text-secondary)] leading-relaxed mb-2"
                  data-testid="resume-review-instructions"
                >
                  {u.resumeReviewInstructions}
                </div>
                <textarea
                  value={resumeText}
                  onChange={(e) => setResumeText(e.target.value)}
                  placeholder={u.resumeReviewEmpty}
                  disabled={!resumeText}
                  rows={12}
                  className="w-full rounded-xl border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] p-3 text-xs whitespace-pre-wrap font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)] disabled:opacity-60"
                  data-testid="resume-review-textarea"
                  aria-label={u.resumeReviewH}
                />
                <label
                  className={`mt-3 flex items-start gap-2 text-xs leading-relaxed cursor-pointer select-none rounded-lg px-2 py-2 -mx-2 transition-colors ${
                    !resumeReviewAcked && resumeText
                      ? "bg-amber-50 ring-1 ring-amber-300 text-[color:var(--text-primary)]"
                      : "text-[color:var(--text-primary)]"
                  }`}
                  data-testid="resume-review-ack-label"
                >
                  <input
                    type="checkbox"
                    checked={resumeReviewAcked}
                    onChange={(e) => handleResumeReviewAck(e.target.checked)}
                    disabled={!resumeText}
                    className="mt-0.5 h-4 w-4 accent-[color:var(--brand)] disabled:opacity-60"
                    data-testid="resume-review-ack-checkbox"
                  />
                  <span>
                    <span className="text-red-700 font-semibold mr-1" aria-hidden="true">*</span>
                    {u.resumeReviewAck}
                  </span>
                </label>
              </div>
            )}

            <JobDescriptionField
              value={jdText}
              onChange={setJdText}
              mode={jdMode}
              onModeChange={(m) => {
                setJdMode(m);
                setJdGeneratedLevel("");
                setJdHasGenerated(false);
                if (m === "sample") setJdText("");
              }}
              sampleGenerationMode={jdSampleGenerationMode}
              onSampleGenerationModeChange={(m) => {
                setJdSampleGenerationMode(m);
                setJdText("");
                setJdGeneratedLevel("");
                setJdHasGenerated(false);
              }}
              resumeReady={Boolean(resumeText && resumeReviewAcked)}
              category={jdCategory}
              onCategoryChange={setJdCategory}
              level={jdLevel}
              onLevelChange={setJdLevel}
              extraContext={jdExtra}
              onExtraContextChange={setJdExtra}
              hasGenerated={jdHasGenerated}
              generating={jdGenerating}
              onGenerate={generateSampleJd}
              u={u}
              categories={sampleCategories}
              levels={sampleLevels}
              lang={accountLang}
            />
          </div>

          <div className="mt-8 rounded-2xl border border-[color:var(--border-stone)] bg-white p-6 space-y-6">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)]">
              {u.sessionH}
            </div>

            <div className="rounded-xl border border-[color:var(--brand)]/20 bg-[color:var(--brand)]/5 p-4 text-sm text-[color:var(--text-primary)] leading-relaxed">
              {u.fixedCountBefore}<strong>{practiceFormat === "advanced" ? 10 : 6}</strong>{u.fixedCountAfterQuick}
            </div>

            {(() => {
              const m = INTERVIEW_MODE_STRINGS[accountLang] || INTERVIEW_MODE_STRINGS.en;
              return (
                <div data-testid="interview-mode-choice">
                  <div className="text-sm text-[color:var(--text-secondary)] mb-3">{m.title}</div>
                  <div className="grid sm:grid-cols-2 gap-3 max-w-2xl" role="radiogroup" aria-label={m.title}>
                    {[
                      { id: "guided", label: m.guidedTitle, sub: m.guidedDesc },
                      { id: "live", label: m.liveTitle, sub: m.liveDesc },
                    ].map((opt) => {
                      const available = modeOptionAvailable(opt.id, practiceFormat);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          role="radio"
                          aria-checked={interviewMode === opt.id}
                          aria-disabled={!available}
                          disabled={!available}
                          data-testid={`mode-${opt.id}`}
                          onClick={() => available && chooseMode(opt.id)}
                          className={`text-left p-4 rounded-xl border transition-colors ${
                            !available
                              ? "border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] opacity-60 cursor-not-allowed"
                              : interviewMode === opt.id
                                ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5"
                                : "border-[color:var(--border-stone)] bg-white hover:bg-[color:var(--bg-surface-hover)]"
                          }`}
                        >
                          <div className="font-semibold tracking-tight">{opt.label}</div>
                          <div className="text-xs text-[color:var(--text-secondary)] mt-1">{opt.sub}</div>
                          {!available && (
                            <div className="text-[11px] font-medium text-[color:var(--brand)] mt-2" data-testid={`mode-${opt.id}-paid-only`}>{m.paidOnly}</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div>
              <div className="flex items-center gap-2 text-sm text-[color:var(--text-secondary)] mb-3">
                <SpeakerHigh weight="duotone" size={16} className="text-[color:var(--brand)]" />
                {u.voiceH}
              </div>
              <div className="grid grid-cols-2 gap-3 max-w-md">
                {[
                  { id: "female", label: u.voiceF, sub: u.voiceFsub },
                  { id: "male", label: u.voiceM, sub: u.voiceMsub },
                ].map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    data-testid={`voice-${v.id}`}
                    onClick={() => setVoice(v.id)}
                    className={`text-left p-4 rounded-xl border transition-colors ${
                      voice === v.id
                        ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5"
                        : "border-[color:var(--border-stone)] bg-white hover:bg-[color:var(--bg-surface-hover)]"
                    }`}
                  >
                    <div className="font-semibold tracking-tight">{v.label}</div>
                    <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                      {v.sub}
                    </div>
                  </button>
                ))}
              </div>
              {voiceSampleOn && (
                <button
                  type="button"
                  onClick={playVoiceSample}
                  disabled={voiceSamplePlaying}
                  data-testid="voice-sample-play"
                  className="mt-3 inline-flex items-center gap-2 rounded-full border border-[color:var(--brand)] px-4 py-1.5 text-sm font-medium text-[color:var(--brand)] hover:bg-[color:var(--brand)]/5 disabled:opacity-60"
                >
                  <SpeakerHigh weight="duotone" size={16} />
                  {voiceSamplePlaying
                    ? (VOICE_SAMPLE_STRINGS[accountLang] || VOICE_SAMPLE_STRINGS.en).playing
                    : (VOICE_SAMPLE_STRINGS[accountLang] || VOICE_SAMPLE_STRINGS.en).hear}
                </button>
              )}
              <audio ref={voiceSampleAudio} className="hidden" />
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm text-[color:var(--text-secondary)] mb-3">
                {showText ? (
                  <Eye weight="duotone" size={16} className="text-[color:var(--brand)]" />
                ) : (
                  <EyeSlash weight="duotone" size={16} className="text-[color:var(--brand)]" />
                )}
                {u.textToggleH}
              </div>
              <div className="grid grid-cols-2 gap-3 max-w-md">
                <button
                  type="button"
                  data-testid="toggle-show-text"
                  onClick={() => setShowText(true)}
                  className={`text-left p-4 rounded-xl border transition-colors ${
                    showText
                      ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5"
                      : "border-[color:var(--border-stone)] bg-white hover:bg-[color:var(--bg-surface-hover)]"
                  }`}
                >
                  <div className="font-semibold tracking-tight">{u.showQ}</div>
                  <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                    {u.showQsub}</div>
                </button>
                <button
                  type="button"
                  data-testid="toggle-hide-text"
                  onClick={() => setShowText(false)}
                  className={`text-left p-4 rounded-xl border transition-colors ${
                    !showText
                      ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5"
                      : "border-[color:var(--border-stone)] bg-white hover:bg-[color:var(--bg-surface-hover)]"
                  }`}
                >
                  <div className="font-semibold tracking-tight">{u.hideQ}</div>
                  <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                    {u.hideQsub}
                  </div>
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-6">
            <Button
              data-testid={UPLOAD.startBtn}
              disabled={
                !resumeText ||
                !resumeReviewAcked ||
                resumeSanitizing ||
                busy ||
                outOfCredits
              }
              onClick={submit}
              className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-6 py-6 text-base"
            >
              {busy
                ? stage === "reading"
                  ? u.startBusyReading
                  : u.startBusyGenerating
                : u.startBtn}
              {!busy && <ArrowRight className="ml-2" size={18} />}
            </Button>
          </div>

          <div className="mt-10 text-xs text-[color:var(--text-secondary)] max-w-md">
            {u.privacyNote}
          </div>
        </section>

        <aside className="lg:col-span-5">
          <div className="sticky top-8 space-y-6">
            <div className="rounded-3xl bg-[color:var(--brand)] text-white p-8">
              <div className="text-xs uppercase tracking-widest text-white/70">
                {u.asideEye}
              </div>
              <div className="mt-2 text-2xl font-semibold tracking-tight leading-snug">
                {u.asideH}
              </div>
            </div>
          </div>
        </aside>
        </div>
      </main>

      <div
        data-testid="mobile-sticky-cta"
        className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[color:var(--border-stone)] px-4 py-3 sm:hidden"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <Button
          data-testid="start-interview-button-mobile"
          disabled={
            !resumeText ||
            !resumeReviewAcked ||
            busy ||
            outOfCredits
          }
          onClick={submit}
          className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base"
        >
          {busy
            ? stage === "reading"
              ? u.startBusyReading
              : u.startBusyGenerating
            : u.startBtn}
          {!busy && <ArrowRight className="ml-2" size={18} />}
        </Button>
      </div>
    </div>
  );
}
